import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
    Leaf,
    Target,
    Star,
    Funnel,
    MagnifyingGlass,
    House,
    Binoculars,
    Trophy,
    BookOpen,
    SignOut,
    ArrowLeft
} from '@phosphor-icons/react';

const CATEGORIES = [
    { id: 'all', label: 'All Missions' },
    { id: 'cleanup', label: 'Cleanup' },
    { id: 'planting', label: 'Planting' },
    { id: 'water', label: 'Water' },
    { id: 'wildlife', label: 'Wildlife' },
    { id: 'education', label: 'Education' },
    { id: 'safety', label: 'Safety' }
];

const ECOSYSTEMS = [
    { id: 'all', label: 'All Ecosystems' },
    { id: 'bush', label: 'Bush' },
    { id: 'reef', label: 'Reef' },
    { id: 'outback', label: 'Outback' }
];

export default function MissionsPage() {
    const { user, api } = useAuth();
    const [missions, setMissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState('all');
    const [ecosystem, setEcosystem] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchMissions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category, ecosystem]);

    const fetchMissions = async () => {
        try {
            let url = '/missions?';
            if (category !== 'all') url += `category=${category}&`;
            if (ecosystem !== 'all') url += `ecosystem=${ecosystem}`;
            
            const response = await api().get(url);
            setMissions(response.data);
        } catch (error) {
            toast.error('Failed to load missions');
        } finally {
            setLoading(false);
        }
    };

    const filteredMissions = missions.filter(mission =>
        mission.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        mission.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const completedMissionIds = user?.completed_missions || [];

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                            <div className="w-10 h-10 rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center">
                                <Leaf weight="fill" className="text-white" size={20} />
                            </div>
                            <span className="font-fredoka text-xl font-bold text-[#1e293b] hidden sm:block">
                                Bush Rangers
                            </span>
                        </Link>
                        
                        <Link 
                            to="/dashboard"
                            className="flex items-center gap-2 text-[#475569] hover:text-[#22c55e] font-semibold"
                            data-testid="back-to-dashboard"
                        >
                            <ArrowLeft weight="bold" size={20} />
                            <span className="hidden sm:inline">Back to Dashboard</span>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="pt-20 px-4 max-w-7xl mx-auto">
                {/* Header */}
                <motion.div 
                    className="mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <h1 className="font-fredoka text-3xl md:text-4xl font-bold text-[#1e293b] mb-2">
                        <Target weight="duotone" className="inline text-[#22c55e] mr-2" size={40} />
                        Missions
                    </h1>
                    <p className="text-[#475569] text-lg">
                        Complete real-world missions to earn Bush Tokens and badges!
                    </p>
                </motion.div>

                {/* Filters */}
                <motion.div 
                    className="mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <div className="brutalist-card p-4 md:p-6">
                        {/* Search */}
                        <div className="relative mb-4">
                            <MagnifyingGlass 
                                weight="bold" 
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#475569]" 
                                size={24} 
                            />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search missions..."
                                className="brutalist-input w-full pl-12"
                                data-testid="search-missions"
                            />
                        </div>

                        {/* Category Filters */}
                        <div className="mb-4">
                            <p className="font-fredoka font-semibold text-[#1e293b] mb-2 flex items-center gap-2">
                                <Funnel weight="duotone" size={20} />
                                Category
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {CATEGORIES.map((cat) => (
                                    <button
                                        key={cat.id}
                                        onClick={() => setCategory(cat.id)}
                                        className={`px-4 py-2 rounded-full font-semibold text-sm transition-all ${
                                            category === cat.id
                                                ? 'bg-[#22c55e] text-white border-4 border-[#1e293b] shadow-[2px_2px_0_0_#1e293b]'
                                                : 'bg-white text-[#1e293b] border-2 border-[#1e293b]/20 hover:border-[#22c55e]'
                                        }`}
                                        data-testid={`filter-category-${cat.id}`}
                                    >
                                        {cat.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Ecosystem Filters */}
                        <div>
                            <p className="font-fredoka font-semibold text-[#1e293b] mb-2">
                                Ecosystem
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {ECOSYSTEMS.map((eco) => (
                                    <button
                                        key={eco.id}
                                        onClick={() => setEcosystem(eco.id)}
                                        className={`px-4 py-2 rounded-full font-semibold text-sm transition-all ${
                                            ecosystem === eco.id
                                                ? 'bg-[#38bdf8] text-white border-4 border-[#1e293b] shadow-[2px_2px_0_0_#1e293b]'
                                                : 'bg-white text-[#1e293b] border-2 border-[#1e293b]/20 hover:border-[#38bdf8]'
                                        }`}
                                        data-testid={`filter-ecosystem-${eco.id}`}
                                    >
                                        {eco.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Missions Grid */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="font-fredoka text-lg">Loading missions...</p>
                    </div>
                ) : filteredMissions.length === 0 ? (
                    <div className="brutalist-card p-8 text-center">
                        <Target weight="duotone" className="text-[#475569] mx-auto mb-4" size={48} />
                        <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-2">
                            No missions found
                        </h3>
                        <p className="text-[#475569]">
                            Try adjusting your filters or search query
                        </p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredMissions.map((mission, index) => {
                            const isCompleted = completedMissionIds.includes(mission.id);
                            return (
                                <motion.div
                                    key={mission.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.05 * index }}
                                >
                                    <Link to={`/missions/${mission.id}`} data-testid={`mission-card-${mission.id}`}>
                                        <div className={`brutalist-card overflow-hidden h-full ${isCompleted ? 'opacity-75' : ''}`}>
                                            <div className={`h-40 relative ${
                                                mission.ecosystem === 'bush' ? 'ecosystem-bush' :
                                                mission.ecosystem === 'reef' ? 'ecosystem-reef' :
                                                'ecosystem-outback'
                                            }`}>
                                                {mission.image_url && (
                                                    <img 
                                                        src={mission.image_url} 
                                                        alt={mission.title}
                                                        className="w-full h-full object-cover opacity-60"
                                                    />
                                                )}
                                                <div className="absolute top-3 left-3 flex gap-2">
                                                    <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 border-[#1e293b] ${
                                                        mission.difficulty === 'easy' ? 'bg-[#22c55e] text-white' :
                                                        mission.difficulty === 'medium' ? 'bg-[#fde047] text-[#1e293b]' :
                                                        'bg-[#fb7185] text-white'
                                                    }`}>
                                                        {mission.difficulty}
                                                    </span>
                                                </div>
                                                {isCompleted && (
                                                    <div className="absolute top-3 right-3 bg-[#22c55e] text-white px-3 py-1 rounded-full text-xs font-bold border-2 border-[#1e293b]">
                                                        ✓ Completed
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-5">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="px-2 py-1 bg-[#f1f5f9] rounded-lg text-xs font-semibold text-[#475569] capitalize">
                                                        {mission.category}
                                                    </span>
                                                    <span className="px-2 py-1 bg-[#f1f5f9] rounded-lg text-xs font-semibold text-[#475569] capitalize">
                                                        {mission.ecosystem}
                                                    </span>
                                                </div>
                                                <h3 className="font-fredoka font-bold text-xl text-[#1e293b] mb-2">
                                                    {mission.title}
                                                </h3>
                                                <p className="text-sm text-[#475569] line-clamp-2 mb-4">
                                                    {mission.description}
                                                </p>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <Star weight="fill" className="text-[#fde047]" size={24} />
                                                        <span className="font-fredoka font-bold text-lg text-[#1e293b]">
                                                            +{mission.xp_reward} Tokens
                                                        </span>
                                                    </div>
                                                    <span className="text-[#22c55e] font-semibold text-sm">
                                                        View Details →
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </main>
        </div>
    );
}
